import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  styled,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import { useTranslation } from "react-i18next";

// Maximum file size: 100MB in bytes
const MAX_FILE_SIZE = 100 * 1024 * 1024;

const DropzoneContainer = styled(Paper)(({ theme }) => ({
  border: `1px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  cursor: "pointer",
  outline: "none",
  transition: "border .24s ease-in-out",
  "&:hover": {
    borderColor: theme.palette.primary.dark,
  },
  position: "relative",
}));

interface FileUploadDropzoneProps {
  onFileAccepted?: (file: File) => void;
  onFileRejected?: (fileRejections: FileRejection[]) => void;
  isLoading?: boolean;
  loadingText?: string;
  sx?: SxProps<Theme>;
}

export default function FileUploadDropzone({
  onFileAccepted,
  onFileRejected,
  isLoading = false,
  loadingText,
  sx,
}: FileUploadDropzoneProps): JSX.Element {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setError(null);

      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors[0].code === "file-too-large") {
          setError(t("dropzone.errorTooLarge"));
        } else if (rejection.errors[0].code === "file-invalid-type") {
          setError(t("dropzone.errorInvalidType"));
        } else {
          setError(t("dropzone.errorGeneric"));
        }

        if (onFileRejected) {
          onFileRejected(rejectedFiles);
        }
        return;
      }

      if (acceptedFiles.length > 0) {
        if (onFileAccepted) {
          onFileAccepted(acceptedFiles[0]);
        }
      }
    },
    [onFileAccepted, onFileRejected],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf", ".PDF"],
    },
    maxSize: MAX_FILE_SIZE,
    multiple: false,
  });

  return (
    <Box sx={{ width: "100%", ...sx }}>
      <DropzoneContainer
        {...getRootProps()}
        elevation={0}
        sx={{
          borderColor: error ? "error.main" : "primary.main",
          backgroundColor: isDragActive ? "action.hover" : "background.default",
          minHeight: "200px",
        }}
      >
        <input {...getInputProps()} />
        {isLoading ? (
          // Loading state
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
            }}
          >
            <CircularProgress size={50} sx={{ mb: 2 }} thickness={4} />
            <Typography color="text.secondary" variant="body2">
              {loadingText ?? t("dropzone.loading")}
            </Typography>
          </Box>
        ) : (
          <>
            <InsertDriveFileOutlinedIcon
              color="primary"
              sx={{ fontSize: 40, mb: 2 }}
            />
            <Typography
              color="primary"
              sx={{
                textDecoration: "underline",
                fontWeight: 500,
                mb: 1,
              }}
              variant="subtitle1"
            >
              {t("dropzone.click")}
            </Typography>
            <Typography variant="subtitle1">{t("dropzone.drag")}</Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
              {t("dropzone.hint")}
            </Typography>
            {error ? (
              <Typography color="error" sx={{ mt: 1 }} variant="caption">
                {error}
              </Typography>
            ) : null}
          </>
        )}
      </DropzoneContainer>
    </Box>
  );
}
